import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check, ChevronDown, ChevronRight, Database, FlaskConical, Loader2, Microscope, Plus,
} from 'lucide-react';
import { listPlaygrounds, createPlayground } from 'features/playground/api/playgroundApi';
import { listDatasets, createDataset, addItems } from 'features/datasets/api/datasetsApi';

/**
 * SpanEvaluateMenu — one launcher for everything you can do with an LLM span.
 *
 * This replaces two sibling dropdowns that used to sit next to each other in the
 * span header (Open in playground, Add to dataset). Two buttons that both mean
 * "take this span somewhere to evaluate it" read as unrelated actions, and the
 * run overlay had only ever grown one of them — so from a run you could reach
 * the playground but not a dataset, for no reason anyone chose.
 *
 * The shape mirrors the Evaluate menu on the Prompt and Dataset pages, so the
 * same verb means the same thing everywhere. Destinations are one hover deeper
 * rather than behind a modal: picking which playground is not a decision worth
 * a dialog.
 */

const SUBMENU_WIDTH = 288;
const MENU_WIDTH = 232;
/**
 * Moving diagonally from a row to its submenu briefly leaves both. Closing on
 * that would make the menu impossible to use with a mouse — this is the standard
 * grace period, short enough not to feel sticky.
 */
const LEAVE_GRACE_MS = 140;

function useClickAway(refs, onAway, active) {
  useEffect(() => {
    if (!active) return undefined;
    const onDoc = (e) => {
      if (refs.some((ref) => ref.current?.contains(e.target))) return;
      onAway();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [refs, onAway, active]);
}

/** A destination list + "create one" field, shared by both submenus. */
function TargetList({
  items, loading, emptyHint, newPlaceholder, busy, error,
  onPick, onCreate, countOf,
}) {
  const [name, setName] = useState('');
  const create = () => { if (name.trim()) onCreate(name.trim()); };

  return (
    <>
      <div className="max-h-56 overflow-y-auto py-1">
        {loading ? (
          <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">Loading…</div>
        ) : null}
        {!loading && !items.length ? (
          <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">{emptyHint}</div>
        ) : null}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => onPick(item)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)] disabled:opacity-50"
          >
            <span className="min-w-0 flex-1 truncate">{item.name || 'Untitled'}</span>
            {countOf ? (
              <span className="flex-none text-[11px] tabular-nums text-[color:var(--text-muted)]">
                {countOf(item)}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1.5 border-t border-[color:var(--border-default)] px-2.5 py-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); e.stopPropagation(); }}
          placeholder={newPlaceholder}
          className="min-w-0 flex-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 py-1 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]"
        />
        <button
          type="button"
          disabled={busy || !name.trim()}
          onClick={create}
          className="inline-flex flex-none items-center gap-1 rounded-md bg-[color:var(--trodo-primary)] px-2 py-1 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90 disabled:opacity-40"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} New
        </button>
      </div>

      {error ? (
        <div className="border-t border-[color:var(--border-default)] px-3 py-1.5 text-[11px] text-[color:var(--feedback-error)]">
          {error}
        </div>
      ) : null}
    </>
  );
}

export default function SpanEvaluateMenu({ spanId, hasPrompt = false, compact = false }) {
  const { team } = useParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null); // 'playground' | 'dataset'
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [rowTop, setRowTop] = useState(0);
  const [flip, setFlip] = useState(false);

  const [playgrounds, setPlaygrounds] = useState(null);
  const [datasets, setDatasets] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(false);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const subRef = useRef(null);
  const leaveTimer = useRef(null);

  const closeAll = useCallback(() => {
    setOpen(false);
    setSubmenu(null);
    setError(null);
  }, []);

  useClickAway(useMemo(() => [triggerRef, menuRef, subRef], []), closeAll, open);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.max(8, Math.min(window.innerWidth - MENU_WIDTH - 8, rect.right - MENU_WIDTH));
      setPos({ top: rect.bottom + 4, left });
      // Put the submenu on whichever side has room. Defaulting to the right and
      // letting it run off-screen is how these menus become unusable on a
      // narrow window or when the span panel is docked right.
      setFlip(left + MENU_WIDTH + SUBMENU_WIDTH + 8 > window.innerWidth);
    };
    place();
    const onKey = (e) => {
      if (e.key === 'Escape') { if (submenu) setSubmenu(null); else closeAll(); }
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, submenu, closeAll]);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  // Each list is fetched the first time its submenu is opened — opening
  // "Evaluate" should not cost two requests you may not need.
  useEffect(() => {
    if (submenu === 'playground' && playgrounds === null) {
      listPlaygrounds().then((d) => setPlaygrounds(d || [])).catch(() => setPlaygrounds([]));
    }
    if (submenu === 'dataset' && datasets === null) {
      listDatasets().then((d) => setDatasets(d || [])).catch(() => setDatasets([]));
    }
  }, [submenu, playgrounds, datasets]);

  const openSubmenu = (key, event) => {
    clearTimeout(leaveTimer.current);
    setError(null);
    const row = event?.currentTarget?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (row && menu) setRowTop(Math.min(row.top - 4, window.innerHeight - 300));
    setSubmenu(key);
  };

  const scheduleClose = () => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setSubmenu(null), LEAVE_GRACE_MS);
  };
  const cancelClose = () => clearTimeout(leaveTimer.current);

  const goToPlayground = (playgroundId) => {
    closeAll();
    navigate(`/${encodeURIComponent(team)}/playground/${playgroundId}?importSpan=${encodeURIComponent(spanId)}`);
  };

  const failed = (e, fallback) => setError(
    e?.response?.data?.message || e?.response?.data?.error || e.message || fallback,
  );

  const createPlaygroundAndGo = async (name) => {
    setBusy(true); setError(null);
    try {
      const pg = await createPlayground(name);
      goToPlayground(pg.id);
    } catch (e) { failed(e, 'Could not create the playground.'); } finally { setBusy(false); }
  };

  const addToDataset = async (datasetId) => {
    setBusy(true); setError(null);
    try {
      await addItems(datasetId, { span_ids: [spanId] });
      closeAll();
      setAdded(true);
      setTimeout(() => setAdded(false), 2200);
    } catch (e) { failed(e, 'Could not add the span.'); } finally { setBusy(false); }
  };

  const createDatasetAndAdd = async (name) => {
    setBusy(true); setError(null);
    try {
      const ds = await createDataset(name);
      await addItems(ds.id, { span_ids: [spanId] });
      setDatasets(null);
      closeAll();
      setAdded(true);
      setTimeout(() => setAdded(false), 2200);
    } catch (e) { failed(e, 'Could not create the dataset.'); } finally { setBusy(false); }
  };

  if (!spanId) return null;

  const size = compact ? 'h-6 px-2 text-[11px]' : 'h-7 px-2.5 text-[12px]';
  const iconSize = compact ? 12 : 13;

  const rowClass = 'flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-[color:var(--surface-hover)]';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? closeAll() : setOpen(true))}
        title="Open this span in the playground, or add it to a dataset"
        className={`inline-flex flex-none items-center gap-1 rounded-md border border-[color:var(--border-default)] bg-surface font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50 ${size}`}
      >
        {added
          ? <Check size={iconSize} className="text-[color:var(--feedback-success)]" />
          : <Microscope size={iconSize} className="text-[color:var(--text-muted)]" />}
        {added ? 'Added' : 'Evaluate'}
        {added ? null : <ChevronDown size={iconSize} className="text-[color:var(--text-muted)]" />}
      </button>

      {open && createPortal(
        <>
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[10002] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-xl"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH, fontFamily: 'Inter, sans-serif' }}
            onMouseLeave={scheduleClose}
            onMouseEnter={cancelClose}
          >
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={submenu === 'playground'}
              className={`${rowClass} ${submenu === 'playground' ? 'bg-[color:var(--surface-hover)]' : ''}`}
              onMouseEnter={(e) => openSubmenu('playground', e)}
              onFocus={(e) => openSubmenu('playground', e)}
              onClick={(e) => openSubmenu('playground', e)}
            >
              <FlaskConical size={15} className="flex-none text-[color:var(--text-muted)]" strokeWidth={1.75} />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-[color:var(--text-primary)]">Open in playground</span>
                <span className="block text-[11px] text-[color:var(--text-muted)]">Replay · tweak · re-run</span>
              </span>
              <ChevronRight size={14} className="flex-none text-[color:var(--text-muted)]" />
            </button>

            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={submenu === 'dataset'}
              className={`${rowClass} ${submenu === 'dataset' ? 'bg-[color:var(--surface-hover)]' : ''}`}
              onMouseEnter={(e) => openSubmenu('dataset', e)}
              onFocus={(e) => openSubmenu('dataset', e)}
              onClick={(e) => openSubmenu('dataset', e)}
            >
              <Database size={15} className="flex-none text-[color:var(--text-muted)]" strokeWidth={1.75} />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-[color:var(--text-primary)]">Add to dataset</span>
                <span className="block text-[11px] text-[color:var(--text-muted)]">Keep it as a test case</span>
              </span>
              <ChevronRight size={14} className="flex-none text-[color:var(--text-muted)]" />
            </button>
          </div>

          {submenu ? (
            <div
              ref={subRef}
              role="menu"
              className="fixed z-[10003] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-xl"
              style={{
                top: rowTop,
                left: flip ? pos.left - SUBMENU_WIDTH - 4 : pos.left + MENU_WIDTH + 4,
                width: SUBMENU_WIDTH,
                fontFamily: 'Inter, sans-serif',
              }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="border-b border-[color:var(--border-default)] px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">
                  {submenu === 'playground' ? 'Open in playground' : 'Add span to dataset'}
                </div>
                <div className="mt-0.5 text-[11px] leading-[1.45] text-[color:var(--text-muted)]">
                  {submenu === 'playground'
                    ? 'Loads this span as a task you can edit and re-run.'
                    : (hasPrompt
                      ? 'Prompt variables and output captured as a test case.'
                      : 'Parsed input and output captured as a test case.')}
                </div>
              </div>

              {submenu === 'playground' ? (
                <TargetList
                  items={playgrounds || []}
                  loading={playgrounds === null}
                  emptyHint="No playgrounds yet: name one below."
                  newPlaceholder="New playground name…"
                  busy={busy}
                  error={error}
                  onPick={(pg) => goToPlayground(pg.id)}
                  onCreate={createPlaygroundAndGo}
                />
              ) : (
                <TargetList
                  items={datasets || []}
                  loading={datasets === null}
                  emptyHint="No datasets yet: name one below."
                  newPlaceholder="New dataset name…"
                  busy={busy}
                  error={error}
                  onPick={(ds) => addToDataset(ds.id)}
                  onCreate={createDatasetAndAdd}
                  countOf={(ds) => ds.item_count ?? 0}
                />
              )}
            </div>
          ) : null}
        </>,
        document.body,
      )}
    </>
  );
}
